import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowRightIcon,
  BellSimpleIcon,
  BuildingsIcon,
  ChartLineIcon,
  CheckIcon,
  DotIcon,
  GearIcon,
  UserIcon,
  UserPlusIcon
} from '@phosphor-icons/react';
import { twMerge } from 'tailwind-merge';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePostHog } from 'src/lib/posthog';
import Button from '../../ui/Button';
import Popover from '../../ui/Popover';
import type { TenantOrganisation } from 'src/features/admin/types';
import {
  CHAMPION_TENANT_MEMBERSHIP_ROLES,
  NON_GUEST_TENANT_MEMBERSHIP_ROLES,
  type TenantMembershipRole
} from 'src/types/auth';
import { useTenantSettingsModal } from './TenantSettingsModalContext';
import useAuth from 'src/hooks/useAuth';
import { prefixWithTenant, resolveTenantCode } from 'src/lib/tenantRouting';
import { useAppDispatch } from 'src/app/store/hooks';
import { toggleMobileMenu } from 'src/redux/ui';
import { apiGet } from 'src/lib/api/axios';
import { snakeToTitleCase } from 'src/lib/utils/formatLabel';
import { formatTenantRole } from 'src/components/org/orgMembers.utils';

/**
 * Small bell indicator used wherever pending admin actions need to be surfaced.
 * Renders as an absolutely-positioned overlay — wrap the parent in `relative`.
 */
export function PendingAlert({ className }: { className?: string }) {
  return (
    <BellSimpleIcon
      size={13}
      weight="fill"
      className={twMerge(
        'text-orange-700 bg-orange-200 rounded-full p-0.5 w-5 h-5 pointer-events-none drop-shadow-sm',
        className
      )}
    />
  );
}

const getPersonalTenantDisplayName = (tenant: TenantOrganisation): string => {
  if (tenant.role !== 'guest') return 'Personal';
  // Legacy format: "Personal (email@example.com)" → "email@example.com's Account"
  const legacyMatch = tenant.name.match(/^Personal \((.+)\)$/);
  if (legacyMatch) return `${legacyMatch[1]}'s Account`;
  // New format: "Joe Bloggs's Account" — already correct
  return tenant.name;
};

const GUEST_AVATAR_COLORS = [
  'bg-rose-100 text-rose-600',
  'bg-amber-100 text-amber-600',
  'bg-emerald-100 text-emerald-600',
  'bg-cyan-100 text-cyan-600',
  'bg-violet-100 text-violet-600',
  'bg-fuchsia-100 text-fuchsia-600'
];

const getGuestAvatarColor = (name: string) => {
  const key = name.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return GUEST_AVATAR_COLORS[key % GUEST_AVATAR_COLORS.length];
};

const getGuestAvatarInitials = (name: string) => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  return words.length >= 2 ? `${words[0][0]}${words[1][0]}`.toUpperCase() : name.slice(0, 2).toUpperCase();
};

const formatSubscriptionTier = (tier?: string | null) => (tier ? snakeToTitleCase(tier) : 'No plan set');

export function TenantLogo({
  name,
  code,
  logoUrl,
  personalVariant,
  className,
  tone = 'default'
}: {
  name: string;
  code: string;
  logoUrl?: string | null;
  personalVariant?: 'own' | 'guest';
  className?: string;
  tone?: 'default' | 'inverse';
}) {
  const [failed, setFailed] = useState(false);
  const sizeClass = className ?? 'h-10 w-10';
  const iconSize = className ? 32 : 18;
  const fallbackClass =
    tone === 'inverse' ? 'bg-primary-lightblue text-secondary-blue' : 'bg-surface-hover text-secondary-blue';

  useEffect(() => {
    setFailed(false);
  }, [logoUrl]);

  if (logoUrl && !failed) {
    return (
      <img
        src={logoUrl}
        alt={`${name} logo`}
        className={`${sizeClass} shrink-0 rounded-lg bg-white object-contain`}
        onError={() => setFailed(true)}
      />
    );
  }

  if (personalVariant === 'own') {
    return (
      <div className={`flex ${sizeClass} shrink-0 items-center justify-center rounded-lg ${fallbackClass}`}>
        <UserIcon size={iconSize} weight="fill" />
      </div>
    );
  }

  if (personalVariant === 'guest') {
    return (
      <div
        className={`flex ${sizeClass} shrink-0 items-center justify-center rounded-lg text-sm font-medium ${getGuestAvatarColor(name)}`}
      >
        {getGuestAvatarInitials(name)}
      </div>
    );
  }

  return (
    <div className={`flex ${sizeClass} shrink-0 items-center justify-center rounded-lg ${fallbackClass}`}>
      <BuildingsIcon size={iconSize} weight="fill" />
    </div>
  );
}

export default function TenantSwitcher({
  variant = 'compact',
  compactSizing = 'fixed',
  tone = 'default'
}: {
  variant?: 'compact' | 'expanded';
  compactSizing?: 'fixed' | 'parent';
  tone?: 'default' | 'inverse';
}) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const posthog = usePostHog();

  const tenantOptions = useMemo(() => (user?.tenants as TenantOrganisation[] | undefined) ?? [], [user?.tenants]);
  const activeTenantCode = resolveTenantCode(pathname, user?.tenants) ?? '';

  const { openTenantSettings } = useTenantSettingsModal();

  const handleTenantSwitch = (code: string) => {
    dispatch(toggleMobileMenu(false));
    const targetPath = prefixWithTenant('/home', code);
    if (typeof window !== 'undefined') {
      window.location.assign(targetPath);
      return;
    }
    navigate(targetPath, { replace: true });
  };

  const handleOrgDashboardClick = (code: string) => {
    if (!code) return;
    dispatch(toggleMobileMenu(false));
    posthog?.capture('navigate_dashboard');
    navigate(prefixWithTenant('/dashboard', code));
  };

  const tenantButtonRef = useRef<HTMLButtonElement>(null);
  const [tenantMenuOpen, setTenantMenuOpen] = useState(false);
  const [logoLoadFailed, setLogoLoadFailed] = useState(false);

  const orderedTenantOptions = useMemo(() => {
    if (!activeTenantCode) return tenantOptions;
    const active = tenantOptions.filter((tenant) => tenant.code === activeTenantCode);
    const others = tenantOptions.filter((tenant) => tenant.code !== activeTenantCode);
    return [...active, ...others];
  }, [tenantOptions, activeTenantCode]);
  const activeTenant = useMemo(
    () => tenantOptions.find((tenant) => tenant.code === activeTenantCode) ?? null,
    [tenantOptions, activeTenantCode]
  );
  const activeTenantLogoUrl = activeTenant?.square_logo_url ?? null;
  const activeTenantPersonalVariant: 'own' | 'guest' | undefined =
    activeTenant?.tenant_type === 'personal' ? (activeTenant.role === 'guest' ? 'guest' : 'own') : undefined;
  const activeTenantName = tenantOptions.find((t) => t.code === activeTenantCode)?.name ?? activeTenantCode ?? '';
  const activeTenantDisplayName = activeTenant
    ? activeTenant.tenant_type === 'personal'
      ? getPersonalTenantDisplayName(activeTenant)
      : activeTenantName
    : activeTenantName;
  const showOrgDashboard =
    !!activeTenant?.role &&
    CHAMPION_TENANT_MEMBERSHIP_ROLES.includes(activeTenant.role as TenantMembershipRole) &&
    activeTenant.tenant_type === 'organisation' &&
    activeTenant.subscription_tier !== 'freemium';

  useEffect(() => {
    setLogoLoadFailed(false);
  }, [activeTenantCode, activeTenantLogoUrl]);

  const isAdmin = activeTenant?.role === 'admin';

  const { data: pendingRequests } = useQuery({
    queryKey: ['membershipRequests', activeTenantCode],
    queryFn: async () => {
      const res = await apiGet<{ id: number }[]>('/tenant-management/membership-requests', {
        meta: { globalError: false, context: 'tenant-switcher-pending-requests' }
      });
      return res.data;
    },
    enabled: isAdmin && !!activeTenantCode,
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: false
  });
  const pendingRequestCount = pendingRequests?.length ?? 0;

  return (
    <>
      <div
        className={twMerge(
          'relative',
          variant === 'compact' && compactSizing === 'fixed' && 'p-2',
          variant === 'compact' && compactSizing === 'parent' && 'w-full'
        )}
      >
        {activeTenant?.role === 'guest' && (
          <span
            className={twMerge(
              'pointer-events-none absolute z-10 whitespace-nowrap rounded-full bg-blue-500 font-semibold leading-none text-white shadow-sm',
              variant === 'compact' ? '-right-1 -top-1 px-1.5 py-px text-[10px]' : 'top-1 right-1 px-2 py-0.5 text-xs'
            )}
          >
            Guest
          </span>
        )}
        {pendingRequestCount > 0 && <PendingAlert className="absolute top-0.5 right-0.5 z-20" />}
        <button
          ref={tenantButtonRef}
          type="button"
          onClick={() => setTenantMenuOpen((value) => !value)}
          className={twMerge(
            variant === 'compact'
              ? twMerge(
                  'flex aspect-square w-full min-h-0 min-w-0 gap-0 items-center rounded-lg border-2 transition-colors group relative flex-col',
                  tone === 'inverse' ? 'border-white/20' : 'border-border-subtle',
                  compactSizing === 'fixed' && 'min-h-[50px] min-w-[50px]'
                )
              : twMerge(
                  'flex min-h-[50px] w-full items-center gap-2 rounded-xl border-2 px-1 py-1 text-left transition-colors',
                  tone === 'inverse'
                    ? 'border-white/20 bg-transparent hover:border-white/40 hover:bg-white/10'
                    : 'border-border-subtle bg-white hover:border-secondary-blue/30 hover:bg-surface-hover'
                ),
            tenantMenuOpen
              ? tone === 'inverse'
                ? 'border-white/40 bg-white/10 text-white font-semibold'
                : 'border-secondary-blue/50 bg-primary-lightblue text-secondary-blue font-semibold'
              : tone === 'inverse'
                ? 'text-white'
                : variant === 'compact'
                  ? 'hover:border-secondary-blue/30 hover:bg-surface-hover text-gray-700'
                  : 'text-gray-700'
          )}
          title={
            activeTenantDisplayName
              ? `Current organisation: ${activeTenantDisplayName}${activeTenant?.role === 'guest' ? ' (Guest)' : ''}`
              : 'Switch organisation'
          }
          aria-label={
            activeTenantDisplayName
              ? `Current organisation: ${activeTenantDisplayName}${activeTenant?.role === 'guest' ? ' (Guest)' : ''}`
              : 'Switch organisation'
          }
        >
          {variant === 'expanded' ? (
            <>
              <TenantLogo
                name={activeTenantDisplayName}
                code={activeTenantCode}
                logoUrl={activeTenantLogoUrl}
                personalVariant={activeTenantPersonalVariant}
                className="h-10 w-10"
                tone={tone}
              />
              <span className="min-w-0 flex-1">
                <span
                  className={twMerge(
                    'block truncate text-sm font-semibold',
                    tone === 'inverse' ? 'text-white' : 'text-content-default'
                  )}
                >
                  {activeTenantDisplayName}
                </span>
                <span
                  className={twMerge(
                    'block truncate text-xs',
                    tone === 'inverse' ? 'text-white/70' : 'text-content-subtle'
                  )}
                >
                  {formatTenantRole(activeTenant?.role)}
                </span>
              </span>
            </>
          ) : activeTenantLogoUrl && !logoLoadFailed ? (
            <img
              src={activeTenantLogoUrl}
              alt={activeTenantDisplayName ? `${activeTenantDisplayName} logo` : 'Organisation logo'}
              className="h-full w-full rounded-lg bg-white object-fill"
              onError={() => setLogoLoadFailed(true)}
            />
          ) : activeTenantPersonalVariant === 'own' ? (
            <span className="flex h-full w-full items-center justify-center">
              <UserIcon
                size={24}
                weight="fill"
                className={tone === 'inverse' ? 'text-primary-lightblue' : 'text-secondary-blue'}
              />
            </span>
          ) : activeTenantPersonalVariant === 'guest' ? (
            <div
              className={`absolute inset-0 flex items-center justify-center rounded-lg text-lg font-medium ${getGuestAvatarColor(activeTenantDisplayName)}`}
            >
              {getGuestAvatarInitials(activeTenantDisplayName)}
            </div>
          ) : (
            <span className="flex h-full w-full items-center justify-center">
              <BuildingsIcon
                size={24}
                weight="fill"
                className={tone === 'inverse' ? 'text-primary-lightblue' : 'text-secondary-blue'}
              />
            </span>
          )}
        </button>
      </div>
      <Popover
        anchorEl={tenantButtonRef.current}
        open={tenantMenuOpen}
        onClose={() => setTenantMenuOpen(false)}
        placement="right-start"
        className="min-w-[400px] p-2"
      >
        {orderedTenantOptions.length === 0 ? (
          <div className="px-2 py-1 text-sm text-content-subtle">No organisations found</div>
        ) : (
          <div className="space-y-2">
            {orderedTenantOptions.map((tenant) => {
              const isCurrentTenant = tenant.code === activeTenantCode;
              const displayName =
                tenant.tenant_type === 'personal' ? getPersonalTenantDisplayName(tenant) : tenant.name;
              const personalVariant: 'own' | 'guest' | undefined =
                tenant.tenant_type === 'personal' ? (tenant.role === 'guest' ? 'guest' : 'own') : undefined;

              return (
                <div
                  key={tenant.code}
                  role={isCurrentTenant ? undefined : 'button'}
                  tabIndex={isCurrentTenant ? undefined : 0}
                  onClick={() => {
                    if (isCurrentTenant) return;
                    setTenantMenuOpen(false);
                    handleTenantSwitch(tenant.code);
                  }}
                  onKeyDown={(e) => {
                    if (isCurrentTenant) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setTenantMenuOpen(false);
                      handleTenantSwitch(tenant.code);
                    }
                  }}
                  aria-current={isCurrentTenant ? 'true' : undefined}
                  className={twMerge(
                    'group relative flex w-full items-start gap-2.5 overflow-hidden rounded-xl border px-2.5 py-2.5 text-left transition-colors',
                    isCurrentTenant
                      ? 'cursor-default border-secondary-blue/30 bg-secondary-blue/[0.03]'
                      : 'cursor-pointer border-border-subtle bg-white hover:border-secondary-blue/20 hover:bg-gray-50'
                  )}
                >
                  {isCurrentTenant && (
                    <div
                      className="absolute right-3 top-3 inline-flex h-5 w-5 items-center justify-center rounded-full bg-secondary-blue text-white"
                      aria-hidden="true"
                    >
                      <CheckIcon size={14} weight="bold" />
                    </div>
                  )}
                  <TenantLogo
                    name={displayName}
                    code={tenant.code}
                    logoUrl={tenant.square_logo_url}
                    personalVariant={personalVariant}
                  />
                  <div className="min-w-0 flex-1 flex flex-col  text-left">
                    <div className="flex items-center gap-2 leading-none">
                      <span
                        className={twMerge(
                          'min-w-0 truncate text-base font-semibold',
                          isCurrentTenant ? 'text-content-default' : 'text-content-subtle'
                        )}
                      >
                        {displayName}
                      </span>
                      {!isCurrentTenant && (
                        <span className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-content-subtlest opacity-0 transition-all group-hover:opacity-100 group-focus-within:opacity-100 group-hover:text-secondary-blue group-focus-within:text-secondary-blue">
                          Switch
                          <ArrowRightIcon size={14} weight="regular" />
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center text-xs text-content-subtlest">
                      {formatTenantRole(tenant.role)}
                      {<DotIcon size={16} weight="light" className="text-content-subtlest" />}
                      {formatSubscriptionTier(tenant.subscription_tier)} Tier
                    </div>

                    {isCurrentTenant && tenant.role !== 'guest' && (
                      <div className="mt-2.5 flex flex-wrap items-center gap-2">
                        {showOrgDashboard && (
                          <Button
                            variant="outline"
                            icon={ChartLineIcon}
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setTenantMenuOpen(false);
                              handleOrgDashboardClick(tenant.code);
                            }}
                          >
                            Usage
                          </Button>
                        )}
                        {tenant.role &&
                          NON_GUEST_TENANT_MEMBERSHIP_ROLES.includes(tenant.role as TenantMembershipRole) && (
                            <>
                              <div className="relative">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  icon={GearIcon}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setTenantMenuOpen(false);
                                    openTenantSettings({
                                      tenant,
                                      initialTabOnOpen: 'organisation',
                                      openInviteModalOnOpen: false
                                    });
                                  }}
                                >
                                  Settings
                                </Button>
                                {pendingRequestCount > 0 && <PendingAlert className="absolute -top-2 -right-2" />}
                              </div>
                              {tenant.tenant_type === 'organisation' &&
                                tenant.subscription_tier !== 'freemium' &&
                                NON_GUEST_TENANT_MEMBERSHIP_ROLES.includes(tenant.role as TenantMembershipRole) && (
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    icon={UserPlusIcon}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setTenantMenuOpen(false);
                                      openTenantSettings({
                                        tenant,
                                        initialTabOnOpen: 'members',
                                        openInviteModalOnOpen: true
                                      });
                                    }}
                                  >
                                    Invite member
                                  </Button>
                                )}
                            </>
                          )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Popover>
    </>
  );
}
